import { useState, type ReactNode } from "react";
import { Clapperboard } from "lucide-react";
import { parseBlockContent } from "../../../lib/blockContentSchemas";
import type { EditorBlock } from "../editor.types";
import { videoEmbed } from "./videoEmbed";
import { captionAlignClass } from "./captionAlign";
import { mediaMat } from "./imageBackgrounds";
import type { MediaResizeChange } from "./mediaSize";
import { MediaResize } from "./MediaResize";

type VideoBlockProps = {
  block: EditorBlock;
  /** The editor: asks for a link when there isn't one. */
  onSetLink?: (url: string) => void;
  /** In the editor: resizing by the bars at its sides. */
  onResize?: (change: MediaResizeChange) => void;
  /**
   * In the editor, whether the block is selected. An unselected player stays
   * still so a click selects the block and it can be dragged; once selected
   * it plays like it will on the site.
   */
  selected?: boolean;
};

/** A YouTube or Vimeo video in one of three styles, like pictures. */
export const VideoBlock = ({ block, onSetLink, onResize, selected }: VideoBlockProps): ReactNode => {
  const content = parseBlockContent("video", block.content);
  // A silent loop is motion nobody asked for, so it waits for a play press
  // when the visitor prefers reduced motion.
  const loop =
    content.playback === "loop" &&
    !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const embed = videoEmbed(content.url, { loop });
  if (!embed) return onSetLink ? <VideoLinkPrompt onSubmit={onSetLink} /> : null;
  // In the editor a loop has nothing to press, so it always stays still.
  const still = onSetLink && (!selected || loop);
  // Shorts are tall: a narrower portrait frame, centred (never edge to edge).
  const bleed = content.style === "bleed" && !embed.portrait;
  const player = (
    <div
      className={`group/video relative overflow-hidden bg-site-panel ${embed.portrait ? "mx-auto aspect-[9/16] w-full max-w-[22rem]" : "aspect-video"} ${bleed ? "[.site-column_&]:rounded-site" : "rounded-site"}`}
    >
      <iframe
        key={embed.src}
        src={embed.src}
        title={content.caption || `${embed.provider} video`}
        className={`absolute inset-0 size-full border-0 ${still ? "pointer-events-none" : ""}`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
      />
      {still && !loop && (
        <span
          aria-hidden="true"
          className="glass pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full border border-hazumu-white/85 px-3 py-1 text-xs whitespace-nowrap text-hazumu-ink opacity-0 transition-opacity duration-150 group-hover/video:opacity-100"
        >
          Click to select, then play
        </span>
      )}
    </div>
  );
  return (
    <figure>
      {content.style === "framed" ? (
        <div className={`rounded-site px-6 py-8 sm:px-10 sm:py-12 ${mediaMat(content.background)}`}>
          <MediaResize size={content.size} width={content.width} onResize={onResize}>{player}</MediaResize>
        </div>
      ) : bleed ? (
        // Edge to edge across the page; inside a column it keeps to the column.
        <div className="mx-[calc(50%-50cqw)] [.site-column_&]:mx-0">{player}</div>
      ) : (
        <MediaResize size={content.size} width={content.width} onResize={onResize}>{player}</MediaResize>
      )}
      {content.caption && (
        <figcaption className={`mt-2.5 text-sm text-site-muted ${captionAlignClass(content.align)}`}>
          {content.caption}
        </figcaption>
      )}
    </figure>
  );
};

/** The empty video block: paste a link to fill it. */
function VideoLinkPrompt({ onSubmit }: { onSubmit: (url: string) => void }) {
  const [link, setLink] = useState("");
  const [error, setError] = useState("");
  return (
    <form
      className="document-image-placeholder"
      onSubmit={(event) => {
        event.preventDefault();
        if (!videoEmbed(link)) {
          setError("That link isn’t a YouTube or Vimeo video.");
          return;
        }
        onSubmit(link.trim());
      }}
    >
      <Clapperboard size={28} strokeWidth={1.5} aria-hidden="true" />
      <strong>Paste a YouTube or Vimeo link</strong>
      <div>
        <input
          type="url"
          aria-label="Video link"
          placeholder="https://youtu.be/…"
          value={link}
          onChange={(event) => {
            setLink(event.target.value);
            setError("");
          }}
          className="min-w-0 flex-1 rounded-site-sm border border-site-border bg-site-surface px-3 py-2 text-sm text-site-text outline-none placeholder:text-site-muted focus:border-site-accent"
        />
        <button type="submit">Add video</button>
      </div>
      {error && <small role="alert">{error}</small>}
    </form>
  );
}
